import type { PetDto } from '@tama/api-contract';
import { useEffect, useRef } from 'react';
import { useCreatePet } from './queries';

/** Every pet is called this. There is no naming step any more. */
export const DEFAULT_PET_NAME = 'Bloby';

/**
 * Hatches a pet the moment the player turns out not to have one.
 *
 * Replaces the naming screen: on a television a text field is a dead end (the
 * WebView swallows the D-pad while one holds focus) and even picking from a
 * list is a step between the player and their pet. So the server's "no pet"
 * answer is treated as an instruction rather than a destination.
 *
 * `pet` is `undefined` while the first fetch is in flight and `null` only once
 * the server has actually said there is none — hatching on `undefined` would
 * fire before we know, and race a pet that already exists into a 409.
 */
export function useAutoHatch(userId: string, pet: PetDto | null | undefined) {
  const createPet = useCreatePet(userId);
  const { mutate } = createPet;

  // One attempt per absence. Without this a failure would be retried on every
  // render, hammering the API with a request that is not going to start working.
  const attempted = useRef(false);

  useEffect(() => {
    // Reset inside the effect rather than during render: a render React discards
    // must not be able to leave this flag cleared behind it.
    if (pet) {
      attempted.current = false;
      return;
    }
    if (pet === undefined || attempted.current) return;
    attempted.current = true;
    mutate({ name: DEFAULT_PET_NAME, species: 'blob' });
  }, [pet, mutate]);

  return {
    ...createPet,
    /** Lets the error screen ask for one more go. */
    retry: () => {
      attempted.current = true;
      mutate({ name: DEFAULT_PET_NAME, species: 'blob' });
    },
  };
}
